import { useState } from 'react';

import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Group,
  Menu,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { notificationsApi, type Notification } from '@/shared/api/notifications';
import { Icon } from '@/shared/components/Icon';
import { formatRelative } from '@/shared/format';

import {
  notificationsRecentQueryOptions,
  notificationsUnreadCountQueryOptions,
  truncateBody,
  unreadBadge,
  useGoToNotification,
  usePortalNotificationTarget,
} from './notificationOptions';

/** Where the bell hangs: the console's header or the portal's. */
export type NotificationBellVariant = 'console' | 'portal';

/**
 * The notification bell (13 §6.2): mounted in `ConsoleHeader` for every session with an active
 * tenant, and in the portal's header with `variant="portal"`. The unread count polls at low
 * frequency and refetches on focus - never a persistent connection - and the recent list is only
 * fetched once the menu actually opens. The portal has no inbox page of its own, so there the whole
 * body is shown and there is no "Ver todas"; a notice leads to the portal's page of what it is
 * about, or nowhere when the portal has none, and never to the console
 * (`portalNotificationTarget`).
 */
export function NotificationBell({ variant = 'console' }: { variant?: NotificationBellVariant }) {
  const { t } = useTranslation(['notifications', 'common']);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const goToInConsole = useGoToNotification();
  const portalTargetOf = usePortalNotificationTarget();
  const [opened, setOpened] = useState(false);
  const inPortal = variant === 'portal';

  const unread = useQuery(notificationsUnreadCountQueryOptions());
  const recent = useQuery({ ...notificationsRecentQueryOptions(), enabled: opened });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: invalidate,
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: invalidate,
  });

  const badge = unreadBadge(unread.data?.page);
  const rows = recent.data?.items ?? [];

  const goTo = async (notification: Notification) => {
    if (!inPortal) {
      await goToInConsole(notification);
      return;
    }
    const target = portalTargetOf(notification);
    if (target !== null) {
      await navigate(target);
    }
  };

  const openNotification = async (notification: Notification) => {
    setOpened(false);
    if (notification.readAt === null) {
      await markRead.mutateAsync(notification.id);
    }
    await goTo(notification);
  };

  return (
    <Menu opened={opened} onChange={setOpened} position="bottom-end" width={360} withinPortal>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label={t('notifications:bell.label')}
          style={{ position: 'relative' }}
        >
          <Icon name="bell" />
          {badge !== null ? (
            <Badge
              size="xs"
              variant="filled"
              color="red"
              radius="xl"
              style={{ position: 'absolute', top: 2, right: 2, pointerEvents: 'none' }}
            >
              {badge}
            </Badge>
          ) : null}
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Group justify="space-between" px="sm" py={6} wrap="nowrap">
          <Text size="sm" fw={600}>
            {t('notifications:bell.title')}
          </Text>
          <Button
            variant="subtle"
            size="compact-xs"
            disabled={badge === null}
            loading={markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            {t('notifications:bell.markAllRead')}
          </Button>
        </Group>
        <Divider />
        <ScrollArea.Autosize mah={360}>
          {recent.isPending ? (
            <Text size="sm" c="dimmed" p="sm">
              {t('common:state.loading')}
            </Text>
          ) : rows.length === 0 ? (
            <Text size="sm" c="dimmed" p="sm">
              {t('notifications:bell.empty')}
            </Text>
          ) : (
            rows.map((notification) => (
              <Menu.Item
                key={notification.id}
                onClick={() => void openNotification(notification)}
                py={8}
                rightSection={
                  inPortal && portalTargetOf(notification) !== null ? (
                    <Icon name="chevronRight" size={14} />
                  ) : null
                }
              >
                <Stack gap={2}>
                  <Group gap={6} wrap="nowrap" align="flex-start">
                    {notification.readAt === null ? (
                      <span
                        aria-hidden="true"
                        style={{
                          marginTop: 6,
                          width: 8,
                          height: 8,
                          flex: 'none',
                          borderRadius: '50%',
                          background: 'var(--mantine-color-steel-5)',
                        }}
                      />
                    ) : null}
                    <Text size="sm" fw={notification.readAt === null ? 600 : 400}>
                      {notification.title}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {inPortal ? notification.body : truncateBody(notification.body)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatRelative(notification.createdAt)}
                  </Text>
                </Stack>
              </Menu.Item>
            ))
          )}
        </ScrollArea.Autosize>
        {inPortal ? null : (
          <>
            <Divider />
            <Menu.Item
              onClick={() => {
                setOpened(false);
                void navigate({ to: '/notifications' });
              }}
            >
              {t('notifications:bell.viewAll')}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
